import { useId } from "react";
import styled from "styled-components";

const Container = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;

  > svg {
    display: block;
    flex-shrink: 0;
  }
`;

// Source artwork viewBox: 110 x 40 (icon + wordmark).
// iconOnly / textOnly crop the shared coordinate space to the relevant region.
const FULL_VIEWBOX = { x: 0, y: 0, w: 110, h: 40 };
const ICON_VIEWBOX = { x: 0, y: 0, w: 27, h: 40 };
const TEXT_VIEWBOX = { x: 34, y: 16, w: 75, h: 23 };

const DEFAULT_HEIGHT = 40;

const Logo = ({
  height = DEFAULT_HEIGHT,
  className,
  onClick,
  iconOnly = false,
  textOnly = false,
  ...rest
}) => {
  const reactId = useId();
  const uid = reactId.replace(/:/g, "");
  const ids = {
    filter: `logo-filter-${uid}`,
    blurGrad: `logo-blurGrad-${uid}`,
    radial: `logo-radial-${uid}`,
    stroke: `logo-stroke-${uid}`,
  };

  const viewBox = iconOnly
    ? ICON_VIEWBOX
    : textOnly
      ? TEXT_VIEWBOX
      : FULL_VIEWBOX;
  const width = (height * viewBox.w) / viewBox.h;

  return (
    <Container className={className} onClick={onClick} {...rest}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
        fill="none"
      >
        {!textOnly && (
          <>
            <path
              d="M5.67482 1.18556C10.7915 -0.64599 11.6227 11.6245 10.2578 14.1035L6.06544 14.8184C5.67546 14.58 0.215242 3.14014 5.67482 1.18556ZM8.45411 14.1035C9.01597 12.2908 8.76469 4.7675 6.45509 5.3799C3.86761 6.06605 7.46448 14.0249 7.62501 14.1992L8.45411 14.1035Z"
              fill="white"
            />
            <path
              d="M15.9353 3.14041C17.0561 2.28801 20.372 1.40546 23.0339 4.87576C24.6451 6.97635 23.9682 9.80826 22.8474 10.3562C21.9121 10.8135 21.5188 10.5704 21.1247 10.4246C20.9838 10.3724 20.8329 10.4391 20.7527 10.5662L19.0408 13.2771C18.9692 13.3904 18.8414 13.4558 18.7077 13.448L14.5681 13.2058C14.3875 13.1951 14.2414 13.051 14.2244 12.8709C13.6221 6.45327 14.281 4.39858 15.9353 3.14041ZM17.1638 7.7908C17.0999 7.7408 17.0088 7.73617 16.9509 7.79275C16.1077 8.61741 15.8523 12.1563 16.5398 12.4138C17.4216 12.7422 19.2425 10.0132 19.3835 9.58279C19.3942 9.55028 19.3765 9.52214 19.3494 9.50076L17.1638 7.7908Z"
              fill="white"
            />
            <g opacity="0.45" filter={`url(#${ids.filter})`}>
              <path
                d="M15.278 12.739L14.908 4.23572C15.0542 5.73714 19.2966 9.57646 20.7914 10.311C20.8417 10.3358 20.8592 10.3974 20.8296 10.4451C20.2782 11.3332 19.1832 13.0947 19.111 13.0947C19.0379 13.0947 16.6322 12.9197 15.3624 12.8267C15.3159 12.8233 15.28 12.7856 15.278 12.739Z"
                fill={`url(#${ids.blurGrad})`}
              />
            </g>
            <rect
              x="0.752806"
              y="12.9801"
              width="25.2671"
              height="25.2671"
              rx="6.07264"
              fill={`url(#${ids.radial})`}
              stroke={`url(#${ids.stroke})`}
              strokeWidth="1.50561"
            />
            <path
              d="M17.349 23.6204C18.9747 24.451 18.9746 26.7761 17.349 27.6067L17.3297 27.6165L11.4471 30.5561C9.9574 31.3006 8.2046 30.2179 8.20459 28.5532V22.6739C8.2046 21.0093 9.9574 19.9266 11.4471 20.6711L17.3297 23.6107L17.349 23.6204Z"
              fill="white"
            />
          </>
        )}
        {!iconOnly && (
          <>
            <path
              d="M98.937 32.2196V21.759C98.937 21.5982 98.9952 21.4593 99.1117 21.3424C99.2282 21.2181 99.3665 21.156 99.5266 21.156H100.815C100.975 21.156 101.113 21.2108 101.23 21.3204C101.346 21.4228 101.405 21.5507 101.405 21.7042V23.4038L101.077 22.9104H101.394C101.743 22.3475 102.234 21.8906 102.868 21.5397C103.508 21.1889 104.211 21.0134 104.975 21.0134C106.198 21.0134 107.155 21.3935 107.846 22.1538C108.538 22.9067 108.884 23.9301 108.884 25.224V32.2196C108.884 32.3877 108.825 32.5339 108.709 32.6582C108.592 32.7751 108.451 32.8336 108.283 32.8336H106.962C106.795 32.8336 106.649 32.7751 106.525 32.6582C106.409 32.5339 106.351 32.3877 106.351 32.2196V25.8161C106.351 25.0704 106.172 24.4674 105.816 24.0068C105.459 23.5463 104.884 23.3161 104.09 23.3161C103.37 23.3161 102.751 23.5719 102.234 24.0836C101.725 24.5953 101.47 25.2715 101.47 26.1121V32.2196C101.47 32.3877 101.408 32.5339 101.284 32.6582C101.168 32.7751 101.026 32.8336 100.859 32.8336H99.5375C99.3774 32.8336 99.2354 32.7751 99.1117 32.6582C98.9952 32.5339 98.937 32.3877 98.937 32.2196Z"
              fill="white"
            />
            <path
              d="M91.1192 32.9758C89.9109 32.9758 88.9647 32.6103 88.2805 31.8793C87.5963 31.1483 87.2542 30.1615 87.2542 28.9188V21.7806C87.2542 21.6052 87.3124 21.459 87.4288 21.342C87.5526 21.2178 87.6945 21.1556 87.8547 21.1556H89.1758C89.3432 21.1556 89.4851 21.2178 89.6016 21.342C89.7253 21.459 89.7872 21.6052 89.7872 21.7806V28.1732C89.7872 28.9846 89.9655 29.6059 90.3222 30.0372C90.6861 30.4685 91.2357 30.6841 91.9709 30.6841C92.757 30.6841 93.3866 30.4137 93.8597 29.8727C94.3329 29.3318 94.5694 28.641 94.5694 27.8004V21.7806C94.5694 21.6052 94.6277 21.459 94.7441 21.342C94.8679 21.2178 95.0135 21.1556 95.1809 21.1556H96.4911C96.6585 21.1556 96.8004 21.2178 96.9169 21.342C97.0406 21.459 97.1025 21.6052 97.1025 21.7806V32.2302C97.1025 32.3837 97.0443 32.5226 96.9278 32.6469C96.8113 32.7711 96.673 32.8333 96.5129 32.8333H95.2245C95.0644 32.8333 94.9261 32.7821 94.8096 32.6798C94.6932 32.5701 94.6349 32.4532 94.6349 32.3289V30.0153L95.6504 31.0789H94.6349C94.2783 31.6856 93.8052 32.1534 93.2156 32.4824C92.626 32.8113 91.9272 32.9758 91.1192 32.9758Z"
              fill="white"
            />
            <path
              d="M80.8188 32.9759C79.8871 32.9759 79.0501 32.7968 78.3076 32.4386C77.5725 32.0804 77.0229 31.587 76.659 30.9583H76.124L76.79 30.4211V32.3838C76.79 32.4934 76.7354 32.5958 76.6262 32.6908C76.5243 32.7858 76.3933 32.8333 76.2331 32.8333H74.9884C74.8283 32.8333 74.6864 32.7749 74.5626 32.6579C74.4462 32.5336 74.3879 32.3947 74.3879 32.2412V17.5921C74.3879 17.4313 74.4498 17.2924 74.5735 17.1754C74.6973 17.0585 74.8429 17 75.0103 17H76.3205C76.4879 17 76.6298 17.0585 76.7463 17.1754C76.8628 17.2924 76.921 17.4313 76.921 17.5921V22.7456L76.8009 22.8443C77.143 22.2814 77.6634 21.8355 78.3622 21.5066C79.0683 21.1776 79.8544 21.0132 80.7206 21.0132C82.2273 21.0132 83.5193 21.5541 84.5966 22.636C85.6739 23.7105 86.2125 25.1177 86.2125 26.8575V27.1206C86.2125 28.8458 85.6848 30.2529 84.6293 31.3421C83.5812 32.4313 82.311 32.9759 80.8188 32.9759ZM80.1965 30.6952C81.1791 30.6952 81.9944 30.3772 82.6422 29.7412C83.29 29.1053 83.6139 28.239 83.6139 27.1425V26.8794C83.6139 25.7902 83.29 24.924 82.6422 24.2807C82.0017 23.6374 81.1864 23.3158 80.1965 23.3158C79.2284 23.3158 78.4241 23.6447 77.7835 24.3026C77.1503 24.9532 76.8336 25.8121 76.8336 26.8794V27.1425C76.8264 28.1586 77.1357 29.0066 77.7617 29.6864C78.3877 30.3589 79.1993 30.6952 80.1965 30.6952Z"
              fill="white"
            />
            <path
              d="M65.5372 36.9999C65.3261 36.9999 65.1624 36.9122 65.0459 36.7368C64.9294 36.5687 64.9294 36.3786 65.0459 36.1666L66.902 32.0219L66.8693 32.8991L62.5019 22.0219C62.4073 21.7953 62.4146 21.5942 62.5238 21.4188C62.6402 21.2434 62.804 21.1556 63.0151 21.1556H64.3908C64.5218 21.1556 64.6383 21.1995 64.7402 21.2872C64.8421 21.3676 64.9149 21.459 64.9586 21.5613L67.8192 28.9846L68.0375 29.6644L68.2559 28.9956L71.1274 21.5613C71.1638 21.4517 71.2366 21.3603 71.3458 21.2872C71.455 21.2068 71.5678 21.1666 71.6843 21.1666H73.049C73.2601 21.1666 73.4203 21.2507 73.5295 21.4188C73.6459 21.5869 73.6568 21.7806 73.5622 21.9999L67.4807 36.6381C67.4443 36.7404 67.3715 36.8245 67.2623 36.8903C67.1604 36.9634 67.0549 36.9999 66.9457 36.9999H65.5372Z"
              fill="white"
            />
            <path
              d="M55.1913 32.9761C53.6991 32.9761 52.4399 32.4352 51.4136 31.3533C50.3945 30.2642 49.885 28.8643 49.885 27.1538V26.8906C49.885 25.1582 50.4055 23.7473 51.4463 22.6582C52.4872 21.5617 53.7683 21.0134 55.2896 21.0134C56.1048 21.0134 56.8727 21.1852 57.5934 21.5288C58.314 21.865 58.8635 22.3402 59.242 22.9542H59.7115L59.0892 23.7766V21.6933C59.0892 21.5544 59.1438 21.4337 59.2529 21.3314C59.3621 21.2218 59.5004 21.1669 59.6678 21.1669H60.978C61.1527 21.1669 61.302 21.2327 61.4257 21.3643C61.5567 21.4886 61.6222 21.6384 61.6222 21.8139V32.2305C61.6222 32.3987 61.5604 32.5412 61.4366 32.6582C61.3201 32.7751 61.1855 32.8336 61.0326 32.8336H59.777C59.6169 32.8336 59.4786 32.7751 59.3621 32.6582C59.2457 32.5339 59.1874 32.3877 59.1874 32.2196V30.2568L59.9299 30.9147H59.3512C58.9873 31.558 58.4304 32.0624 57.6807 32.4279C56.9383 32.7934 56.1085 32.9761 55.1913 32.9761ZM55.8027 30.7283C56.8 30.7283 57.6116 30.3921 58.2375 29.7196C58.8708 29.0397 59.1874 28.1845 59.1874 27.1538V26.9016C59.1874 25.827 58.8635 24.9608 58.2157 24.3029C57.5752 23.645 56.7708 23.3161 55.8027 23.3161C54.7983 23.3161 53.9721 23.6377 53.3243 24.281C52.6837 24.9242 52.3635 25.7978 52.3635 26.9016V27.1538C52.3635 28.2576 52.6874 29.1311 53.3352 29.7744C53.9903 30.4104 54.8128 30.7283 55.8027 30.7283Z"
              fill="white"
            />
            <path
              d="M46.7533 32.8333C46.5859 32.8333 46.4403 32.7749 46.3165 32.6579C46.2001 32.5336 46.1418 32.3874 46.1418 32.2193V17.614C46.1418 17.4459 46.2001 17.3034 46.3165 17.1864C46.4403 17.0621 46.5859 17 46.7533 17H48.0744C48.2345 17 48.3728 17.0621 48.4893 17.1864C48.613 17.3034 48.6749 17.4459 48.6749 17.614V32.2193C48.6749 32.3874 48.613 32.5336 48.4893 32.6579C48.3728 32.7749 48.2345 32.8333 48.0744 32.8333H46.7533Z"
              fill="white"
            />
            <path
              d="M34.6769 32.8444C34.4877 32.8444 34.3276 32.7786 34.1965 32.6471C34.0655 32.5082 34 32.3474 34 32.1646V17.7786C34 17.5886 34.0691 17.4241 34.2074 17.2852C34.3457 17.139 34.5059 17.0659 34.6879 17.0659H39.885C41.4572 17.0659 42.7201 17.5155 43.6736 18.4146C44.6272 19.3137 45.1039 20.582 45.1039 22.2194V22.362C45.1039 23.9994 44.6272 25.2604 43.6736 26.1449C42.7274 27.0294 41.4536 27.4716 39.8522 27.4716H35.6268V25.0922H39.6666C40.5182 25.0922 41.1806 24.8802 41.6538 24.4563C42.1269 24.0323 42.3634 23.3452 42.3634 22.3949V22.2414C42.3634 21.3276 42.105 20.6624 41.5882 20.2457C41.0714 19.8291 40.3763 19.6207 39.5028 19.6207H36.7951V32.1646C36.7951 32.3474 36.7296 32.5082 36.5986 32.6471C36.4675 32.7786 36.311 32.8444 36.1291 32.8444H34.6769Z"
              fill="white"
            />
          </>
        )}
        <defs>
          <filter
            id={ids.filter}
            x="12.8285"
            y="2.1563"
            width="10.0949"
            height="13.0178"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="1.03971"
              result="effect1_foregroundBlur"
            />
          </filter>
          <linearGradient
            id={ids.blurGrad}
            x1="19.0176"
            y1="9.25887"
            x2="16.8472"
            y2="12.7734"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopOpacity="0.16" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <radialGradient
            id={ids.radial}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(13.3864 25.6137) rotate(90) scale(13.3864)"
          >
            <stop stopColor="#2D78FF" />
            <stop offset="1" stopColor="#4D9DFF" />
          </radialGradient>
          <linearGradient
            id={ids.stroke}
            x1="13.3864"
            y1="12.2273"
            x2="13.3864"
            y2="39"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#2D78FF" />
            <stop offset="1" stopColor="#89BEFE" />
          </linearGradient>
        </defs>
      </svg>
    </Container>
  );
};

export default Logo;
